import { useEffect, useState } from 'react'
import {
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  message,
  Popconfirm,
  Select,
  Tag,
  Empty
} from 'antd'
import { PlusOutlined, EditOutlined, DeleteOutlined, UploadOutlined } from '@ant-design/icons'
import { sensitiveWordApi } from '../api/fragment'
import { SensitiveWordInfo } from '../types'

const { Option } = Select
const { TextArea } = Input

const CATEGORIES = ['violence', 'porn', 'politics', 'advertisement', 'other']
const CATEGORY_LABELS: Record<string, string> = {
  violence: '暴力',
  porn: '色情',
  politics: '政治',
  advertisement: '广告',
  other: '其他'
}

const CATEGORY_COLORS: Record<string, string> = {
  violence: 'red',
  porn: 'orange',
  politics: 'blue',
  advertisement: 'purple',
  other: 'default'
}

export default function SensitiveWordManage() {
  const [words, setWords] = useState<SensitiveWordInfo[]>([])
  const [loading, setLoading] = useState(false)
  const [modalType, setModalType] = useState<'add' | 'edit' | 'import' | null>(null)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>()
  const [form] = Form.useForm()
  const [importForm] = Form.useForm()

  const loadWords = async () => {
    setLoading(true)
    try {
      const result = await sensitiveWordApi.getList(selectedCategory)
      setWords(result)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadWords()
  }, [selectedCategory])

  const handleAdd = async (values: { word: string; category?: string }) => {
    try {
      await sensitiveWordApi.add(values)
      message.success('添加成功')
      setModalType(null)
      form.resetFields()
      loadWords()
    } catch (e) {
      console.error(e)
    }
  }

  const handleEdit = async (values: { word: string; category?: string }) => {
    if (!editingId) return
    try {
      await sensitiveWordApi.edit(editingId, values)
      message.success('编辑成功')
      setModalType(null)
      setEditingId(null)
      form.resetFields()
      loadWords()
    } catch (e) {
      console.error(e)
    }
  }

  const handleDelete = async (id: number) => {
    try {
      await sensitiveWordApi.delete(id)
      message.success('删除成功')
      loadWords()
    } catch (e) {
      console.error(e)
    }
  }

  const handleBatchImport = async (values: { wordsStr: string }) => {
    const wordList = values.wordsStr
      .split(/[,\n，]/)
      .map((w) => w.trim())
      .filter((w) => w.length > 0)

    if (wordList.length === 0) {
      message.error('请输入要导入的敏感词')
      return
    }

    try {
      const count = await sensitiveWordApi.batchImport({ words: wordList })
      message.success(`成功导入 ${count} 个敏感词`)
      setModalType(null)
      importForm.resetFields()
      loadWords()
    } catch (e) {
      console.error(e)
    }
  }

  const openEditModal = (item: SensitiveWordInfo) => {
    setEditingId(item.id)
    form.setFieldsValue({
      word: item.word,
      category: item.category
    })
    setModalType('edit')
  }

  const columns = [
    {
      title: '敏感词',
      dataIndex: 'word',
      key: 'word',
    },
    {
      title: '分类',
      dataIndex: 'category',
      key: 'category',
      render: (category: string) => (
        <Tag color={CATEGORY_COLORS[category] || 'default'}>
          {CATEGORY_LABELS[category] || category}
        </Tag>
      ),
    },
    {
      title: '创建者',
      dataIndex: 'createUserNickname',
      key: 'createUserNickname',
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      key: 'createTime',
    },
    {
      title: '操作',
      key: 'actions',
      render: (_: any, record: SensitiveWordInfo) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => openEditModal(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定删除吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="text" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">敏感词管理</h2>
        <Space>
          <Select
            placeholder="分类筛选"
            style={{ width: 150 }}
            allowClear
            value={selectedCategory}
            onChange={setSelectedCategory}
          >
            {CATEGORIES.map((cat) => (
              <Option key={cat} value={cat}>{CATEGORY_LABELS[cat]}</Option>
            ))}
          </Select>
          <Button
            icon={<UploadOutlined />}
            onClick={() => setModalType('import')}
          >
            批量导入
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setModalType('add')}
          >
            添加敏感词
          </Button>
        </Space>
      </div>

      <Table
        columns={columns}
        dataSource={words}
        loading={loading}
        rowKey="id"
        locale={{ emptyText: <Empty description="暂无敏感词" /> }}
      />

      <Modal
        title={modalType === 'add' ? '添加敏感词' : '编辑敏感词'}
        open={modalType === 'add' || modalType === 'edit'}
        onCancel={() => {
          setModalType(null)
          setEditingId(null)
          form.resetFields()
        }}
        onOk={() => form.submit()}
        okText="确定"
        cancelText="取消"
      >
        <Form
          form={form}
          onFinish={modalType === 'add' ? handleAdd : handleEdit}
          labelCol={{ span: 6 }}
        >
          <Form.Item
            label="敏感词"
            name="word"
            rules={[{ required: true, message: '请输入敏感词' }]}
          >
            <Input placeholder="请输入敏感词" />
          </Form.Item>
          <Form.Item
            label="分类"
            name="category"
          >
            <Select placeholder="请选择分类">
              {CATEGORIES.map((cat) => (
                <Option key={cat} value={cat}>{CATEGORY_LABELS[cat]}</Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="批量导入敏感词"
        open={modalType === 'import'}
        onCancel={() => {
          setModalType(null)
          importForm.resetFields()
        }}
        onOk={() => importForm.submit()}
        okText="导入"
        cancelText="取消"
      >
        <Form
          form={importForm}
          onFinish={handleBatchImport}
        >
          <Form.Item
            name="wordsStr"
            rules={[{ required: true, message: '请输入敏感词' }]}
          >
            <TextArea
              rows={8}
              placeholder="请输入敏感词，多个用逗号或换行分隔"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
